import React, { useState, useEffect, useReducer, useRef } from "react";
import { Realtime } from "ably";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Avatar,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import OnlineStatus from "../components/OnlineStatus";
import apilink from "../globals/Baseurls";
import MicIcon from "@mui/icons-material/Mic";
import CircularProgress from "@mui/material/CircularProgress";

/* ---------------- REDUCER ---------------- */

const messagesReducer = (state, action) => {
  switch (action.type) {
    case "SET":
      return action.payload;

    case "ADD":
      return [...state, action.payload];

    case "MARK_READ":
      return state.map((m) =>
        m.user_id !== action.myId ? { ...m, is_read: true } : m
      );

    default:
      return state;
  }
};

/* ---------------- COMPONENT ---------------- */

export default function ChatRoom() {
  const navigate = useNavigate();
  const bottomRef = useRef(null);
  const ablyRef = useRef(null);
  const channelRef = useRef(null);
  const [sending, setSending] = useState(false);

  const userData = JSON.parse(localStorage.getItem("userSessionData"));
  const myId = Number(userData.id);
  const token = userData.token;

  const [conversation, setConversation] = useState(null);
  const [messages, dispatch] = useReducer(messagesReducer, []);
  const [newMsg, setNewMsg] = useState("");
  const [file, setFile] = useState(null);
  const [fileMessage, setFileMessage] = useState("");
  const [typing, setTyping] = useState(false);

  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Start recording
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    setMediaRecorder(recorder);
    setRecording(true);

    const chunks = [];
    recorder.ondataavailable = (e) => chunks.push(e.data);
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: "audio/webm" });
      const file = new File([blob], "audio.mp3", { type: "audio/mp3" });
      setAudioBlob(file);
    };

    recorder.start();
  };

  //Stop recording
  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setRecording(false);
    }
  };

  const deleteRecording = () => setAudioBlob(null);

  /* ---------------- INIT CONVERSATION ---------------- */

  useEffect(() => {
    const chatData = JSON.parse(localStorage.getItem("chatSessionData"));

    if (!chatData) return;

    setConversation({
      id: chatData.conversation_id,
      recipient_id: chatData.user_id,
      recipient_name: chatData.name,
      recipient_photo: chatData.photo,
    });
  }, []);

  /* ---------------- FETCH MESSAGES ---------------- */

  useEffect(() => {
    if (!conversation?.id) return;

    const loadMessages = async () => {
      const res = await axios.get(
        `${apilink.link}/chat/${conversation.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      dispatch({ type: "SET", payload: res.data });

      // Mark read ONCE when opening
      await axios.post(
        `${apilink.link}/chat/read`,
        { conversation_id: conversation.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      dispatch({ type: "MARK_READ", myId });
    };

    loadMessages();
  }, [conversation?.id]);

  /* ---------------- ABLY (ONCE PER CONVERSATION) ---------------- */

  useEffect(() => {
    if (!conversation?.id) return;

    ablyRef.current = new Realtime({
      key: "DLUodA.c6Iz-Q:q5WKX3aLmbhf8oJxchkz2L-pbwbCae2DDtHJIg6rU84",
    });

    channelRef.current = ablyRef.current.channels.get(
      `conversation-${conversation.id}`
    );

    const onMessage = async (msg) => {
      dispatch({ type: "ADD", payload: msg.data });

      // Incoming message → mark all previous as read
      if (msg.data.user_id !== myId) {
        await axios.post(
          `${apilink.link}/chat/read`,
          { conversation_id: conversation.id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        dispatch({ type: "MARK_READ", myId });
      }
    };

    const onTyping = (msg) => {
      if (msg.data.user_id !== myId) {
        setTyping(true);
        setTimeout(() => setTyping(false), 1500);
      }
    };

    channelRef.current.subscribe("new_message", onMessage);
    channelRef.current.subscribe("typing", onTyping);

    return () => {
      channelRef.current.unsubscribe();
      ablyRef.current.close();
    };
  }, [conversation?.id]);

  /* ---------------- AUTO SCROLL ---------------- */

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  /* ---------------- SEND MESSAGE (TEXT + FILE) ---------------- */

  const sendMessageOLd = async () => {
    if (!newMsg.trim() && !file) return;

    try {
      setSending(true);

      const formData = new FormData();
      formData.append("conversation_id", conversation.id);
      formData.append(
        "type",
        file ? file.type.split("/")[0] : "text"
      );
      formData.append("body", file ? fileMessage : newMsg);

      if (file) formData.append("file", file);

      await axios.post(`${apilink.link}/chat/send`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setNewMsg("");
      setFile(null);
      setFileMessage("");

    } catch (error) {
      console.error("Message send failed:", error);
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async () => {
    if (!newMsg.trim() && !file && !audioBlob) return;

    try {
      setSending(true);

      const formData = new FormData();
      formData.append("conversation_id", conversation.id);

      if (audioBlob) {
        formData.append("type", "voice");
        formData.append("file", audioBlob);
      } else if (file) {
        formData.append("type", file.type.split("/")[0]);
        formData.append("body", fileMessage || newMsg);
        formData.append("file", file);
      } else {
        formData.append("type", "text");
        formData.append("body", newMsg);
      }

      await axios.post(`${apilink.link}/chat/send`, formData, {
        headers: { Authorization: `Bearer ${token}` }, // let axios handle multipart boundary
      });

      // Reset after sending
      setNewMsg("");
      setFile(null);
      setFileMessage("");
      setAudioBlob(null);
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  /* ---------------- HELPER FUNCTION ---------------- */
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  /* ---------------- RENDER ---------------- */

  if (!conversation) return null;

  // console.log(messages);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* HEADER */}
      <Box sx={{ display: "flex", alignItems: "center", p: 2 }}>
        {/* Back button */}
        <IconButton onClick={() => navigate("/chats")}>
          <ArrowBackIcon />
        </IconButton>

        {/* Avatar */}
        <Avatar
          src={`${apilink.base_link}/storage/${conversation.recipient_photo}`}
          sx={{ mx: 1, cursor: "pointer" }}
          onClick={() => navigate("/user-profile", { state: { userId: conversation.recipient_id } })}
        />

        {/* Name & Status */}
        <Box
          sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 1 }}
          onClick={() =>
            navigate("/user-profile", { state: { userId: conversation.recipient_id } })
          }
        >
          {/* Online status dot */}
          <OnlineStatus userId={conversation.recipient_id} size={12} />

          {/* Name */}
          <Typography variant="h6" sx={{ margin: 0 }}>
            {conversation.recipient_name || "Chat"}
          </Typography>

        </Box>

      </Box>


      {/* MESSAGES */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: "#f4f6fb" }}>
        {messages.map((m) => (
          <Box key={m.id} sx={{ display: "flex", justifyContent: m.user_id === myId ? "flex-end" : "flex-start", mb: 1 }}>
            <Paper sx={{ p: 1.5, maxWidth: "75%", bgcolor: m.user_id === myId ? "#010832" : "#fff", color: m.user_id === myId ? "#fff" : "#000", borderRadius: 2 }}>
              {m.body && <Typography sx={{ mb: m.type !== "text" ? 0.5 : 0 }}>{m.body}</Typography>}

              {m.type === "image" && (
                <img src={`${apilink.base_link}/storage/${m.file_path}`} alt="image" style={{ maxWidth: "100%", borderRadius: 4 }} />
              )}

              {m.type === "audio" && (
                <div>
                  <audio controls style={{ maxWidth: "100%", height: 50 }}>
                    <source src={`${apilink.base_link}/storage/${m.file_path}`} type="audio/mpeg" />
                    Your browser does not support the audio element. <a href={`${apilink.base_link}/storage/${m.file_path}`} target="_blank" rel="noreferrer">Download audio</a>
                  </audio>
                </div>
              )}

              <Typography variant="caption" sx={{ display: "block", mt: 0.5, textAlign: "right", opacity: 0.7 }}>
                {formatDateTime(m.created_at)}
              </Typography>
            </Paper>
          </Box>
        ))}
        <div ref={bottomRef} />
      </Box>

      {/* INPUT */}
      {file ? (
        <Paper
          sx={{
            p: 2,
            m: 1,
            bgcolor: "#f9f9f9",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          {/* HEADER */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="subtitle2">
              Preview ({file.name})
            </Typography>
            <IconButton onClick={() => { setFile(null); setFileMessage(""); }}>
              ✕
            </IconButton>
          </Box>

          {/* Recording  PREVIEW */}
          {audioBlob && (
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <audio controls src={URL.createObjectURL(audioBlob)} />
              <IconButton onClick={deleteRecording}>
                ❌
              </IconButton>
            </Box>
          )}
          {/* IMAGE PREVIEW */}
          {file.type.startsWith("image/") && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <img
                src={URL.createObjectURL(file)}
                alt="preview"
                style={{ maxHeight: "120px", borderRadius: "4px" }}
              />
            </Box>
          )}

          {/* MESSAGE + BUTTONS */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Add a message..."
              value={fileMessage}
              onChange={(e) => setFileMessage(e.target.value)}
            // onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            />

            <Button
              variant="contained"
              onClick={sendMessage}
              disabled={sending}
              sx={{
                bgcolor: "#010832",
                position: "relative",
                minWidth: 50,
              }}
            >
              {sending ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : (
                <SendIcon />
              )}
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={() => { setFile(null); setFileMessage(""); }}
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      ) : (
        <Box sx={{ p: 1, display: "flex", flexDirection: "column", bgcolor: "#fff" }}>

          {/* Audio preview */}
          {audioBlob && (
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <audio controls src={URL.createObjectURL(audioBlob)} />
              <IconButton onClick={deleteRecording}>
                ❌
              </IconButton>
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* Attach file */}
            <IconButton component="label">
              <AttachFileIcon />
              <input
                type="file"
                hidden
                onChange={(e) => setFile(e.target.files[0])}
              />
            </IconButton>

            {/* Record / Stop */}
            {!recording ? (
              <IconButton onClick={startRecording}>
                <MicIcon />
              </IconButton>
            ) : (
              <IconButton onClick={stopRecording}>
                ⏹
              </IconButton>
            )}

            {/* Text input */}
            <TextField
              fullWidth
              size="small"
              placeholder="Type a message..."
              value={newMsg}
              onChange={(e) => setNewMsg(e.target.value)}
              sx={{ mx: 1 }}
            />

            {/* Send button */}
            <Button variant="contained"
              onClick={sendMessage}
              disabled={sending}
              sx={{ bgcolor: "#010832" }}
            >
              {sending ? <CircularProgress size={20} sx={{ color: "white" }} /> : <SendIcon />}
            </Button>
          </Box>
        </Box>
      )}

    </Box>
  );
}
